import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Phone, PhoneCall, PhoneOutgoing, PhoneIncoming, Settings, Shield, MessageSquare, Bell,
  Clock, Mic, Ban, Mail, Volume2, Users, Trash2, Plus, Save, ArrowRight,
  Voicemail, Globe, UserCheck, Briefcase, Zap, ChevronRight, Check, Building, Smartphone
} from "lucide-react";
import { API } from "../../App";
import { getAuthConfig } from "../../utils/api";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "sonner";

const SECTIONS = [
  { id: "general", label: "General", icon: Settings },
  { id: "call", label: "Call Handling", icon: PhoneCall },
  { id: "routing", label: "In-App Calling", icon: Smartphone },
  { id: "sms", label: "SMS & Messaging", icon: MessageSquare },
  { id: "voicemail", label: "Voicemail", icon: Voicemail },
  { id: "recording", label: "Recording", icon: Mic },
  { id: "spam", label: "Spam & Blocking", icon: Shield },
  { id: "notifications", label: "Notifications", icon: Bell },
];

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const buildSchedule = (stored, legacy) => {
  const start = legacy?.start || "09:00";
  const end = legacy?.end || "17:00";
  const out = {};
  DAY_ORDER.forEach((d, i) => { out[d] = (stored || {})[d] || { enabled: i < 5, start, end }; });
  return out;
};

const PhoneConfigModal = ({ open, onOpenChange, phone, jobSources, onSaved }) => {
  const { isDark } = useTheme();
  const [activeSection, setActiveSection] = useState("general");
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [eligibleRecipients, setEligibleRecipients] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/store/locations`, getAuthConfig())
      .then((r) => setLocations(r.data?.locations || []))
      .catch(() => {});
  }, [open]);

  // ─── Form state ──────────────────────────────────────────────
  const [config, setConfig] = useState({});

  useEffect(() => {
    if (open && phone) {
      const c = phone.config || {};
      const cf = phone.call_flow || {};
      setConfig({
        // General
        label: phone.label || "",
        purpose: phone.purpose || "main",
        assignment_type: phone.assignment_type || "business",
        assignment_id: phone.assignment_id || "",
        location_id: phone.location_id || "",
        is_active: phone.is_active !== false,
        // Per-number Call Routing (in-app calling)
        call_routing_enabled: phone.call_routing_enabled === true,
        auto_create_job_from_call: phone.auto_create_job_from_call === true,
        // Call flow (preserve existing)
        call_flow_type: cf.type || "forward",
        in_app_recipients: cf.recipients || [],
        in_app_ring_timeout: cf.ring_timeout || 25,
        in_app_fallback: cf.fallback || "voicemail",
        in_app_ring_strategy: cf.ring_strategy || "everyone",
        in_app_step_timeout: cf.step_timeout || 15,
        fallback_forward_to: cf.fallback_forward_to || "",
        forward_to: cf.forward_to || "",
        ring_timeout: c.ring_timeout || 30,
        simultaneous_ring: c.simultaneous_ring || false,
        simultaneous_numbers: c.simultaneous_numbers || [],
        ivr_greeting: cf.ivr_greeting || "Thank you for calling.",
        ivr_menu: cf.ivr_menu || [],
        ivr_default_action: cf.ivr_default_action || "hangup",
        ivr_default_forward_to: cf.ivr_default_forward_to || "",
        round_robin_numbers: cf.round_robin_numbers || [],
        business_hours: cf.business_hours || { start: "09:00", end: "17:00" },
        business_hours_enabled: cf.business_hours_enabled === true,
        business_hours_schedule: buildSchedule(cf.business_hours_schedule, cf.business_hours),
        after_hours_action: ["voicemail", "forward", "ring_anyway"].includes(cf.after_hours_action) ? cf.after_hours_action : "voicemail",
        after_hours_forward_to: cf.after_hours_forward_to || c.after_hours_forward || "",
        after_hours_greeting: cf.after_hours_greeting || c.after_hours_message || "Thanks for calling. We're currently closed — please leave a message after the beep.",
        whisper_message: cf.whisper_message || "",
        call_screening: c.call_screening || false,
        call_queue: c.call_queue || false,
        call_queue_music: c.call_queue_music || "default",
        // SMS
        sms_auto_reply_enabled: c.sms_auto_reply_enabled || false,
        sms_auto_reply_message: c.sms_auto_reply_message || "Thanks for your message! We'll get back to you shortly.",
        sms_after_hours_reply: c.sms_after_hours_reply || false,
        sms_after_hours_message: c.sms_after_hours_message || "We're currently closed. We'll respond when we reopen.",
        missed_call_sms: c.missed_call_sms || false,
        missed_call_sms_message: c.missed_call_sms_message || "Sorry we missed your call! How can we help you?",
        sms_signature: c.sms_signature || "",
        sms_opt_out_enabled: c.sms_opt_out_enabled !== false,
        keyword_responses: c.keyword_responses || [],
        // Voicemail
        voicemail_enabled: c.voicemail_enabled !== false,
        voicemail_greeting: c.voicemail_greeting || "Please leave a message after the beep.",
        voicemail_to_email: c.voicemail_to_email || false,
        voicemail_email: c.voicemail_email || "",
        voicemail_transcription: c.voicemail_transcription || false,
        voicemail_max_length: c.voicemail_max_length || 120,
        // Recording
        call_recording: c.call_recording || false,
        recording_consent_message: c.recording_consent_message || "This call may be recorded for quality assurance.",
        recording_consent_enabled: c.recording_consent_enabled || false,
        // Caller ID
        caller_id_name: c.caller_id_name || "",
        inbound_caller_id: c.inbound_caller_id || "business",
        hold_music: c.hold_music || "default",
        // Spam & Blocking
        spam_filter: c.spam_filter || false,
        block_international: c.block_international || false,
        blocked_numbers: c.blocked_numbers || [],
        // Notifications
        missed_call_email: c.missed_call_email || false,
        missed_call_email_address: c.missed_call_email_address || "",
        daily_summary: c.daily_summary || false,
        daily_summary_email: c.daily_summary_email || "",
        realtime_notifications: c.realtime_notifications || false,
        notify_team_members: c.notify_team_members || [],
      });
      setActiveSection("general");
      fetchTeamData();
    }
  }, [open, phone]);

  const fetchTeamData = async () => {
    try {
      const [techRes, teamRes, eligRes] = await Promise.all([
        axios.get(`${API}/technicians`, getAuthConfig()),
        axios.get(`${API}/team`, getAuthConfig()).catch(() => ({ data: { members: [] } })),
        axios.get(`${API}/phone/voice-client/eligible-recipients`, getAuthConfig()).catch(() => ({ data: { recipients: [] } })),
      ]);
      setTechnicians(techRes.data || []);
      setDispatchers((teamRes.data?.members || []).filter(m => m.role === "dispatcher"));
      setEligibleRecipients(eligRes.data?.recipients || []);
    } catch { /* silent */ }
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    if (!phone) return;
    setSaving(true);
    try {
      const { call_flow_type, forward_to, ivr_greeting, ivr_menu,
        ivr_default_action, ivr_default_forward_to,
        round_robin_numbers,
        business_hours, after_hours_action, whisper_message,
        // ── Pull these out of `rest` so they save at TOP-LEVEL on the
        // phone doc (not inside `config`) — the inbound webhook reads them
        // from the top level.
        call_routing_enabled, auto_create_job_from_call,
        in_app_recipients, in_app_ring_timeout, in_app_fallback, fallback_forward_to,
        in_app_ring_strategy, in_app_step_timeout,
        business_hours_enabled, business_hours_schedule,
        after_hours_forward_to, after_hours_greeting,
        ...rest } = config;
      const callFlow = {
        type: call_flow_type,
        forward_to,
        ivr_greeting,
        ivr_menu,
        ivr_default_action,
        ivr_default_forward_to,
        round_robin_numbers,
        business_hours,
        after_hours_action,
        whisper_message,
        // In App Phone System routing
        recipients: config.in_app_recipients || [],
        ring_timeout: Number(config.in_app_ring_timeout) || 25,
        fallback: config.in_app_fallback || "voicemail",
        fallback_forward_to: config.fallback_forward_to || "",
        voicemail_greeting: config.voicemail_greeting,
        // Ring strategy (Phase 2)
        ring_strategy: config.in_app_ring_strategy || "everyone",
        step_timeout: Number(config.in_app_step_timeout) || 15,
        // Business hours routing (Phase 2)
        business_hours_enabled: !!config.business_hours_enabled,
        business_hours_schedule: config.business_hours_schedule || {},
        after_hours_forward_to: config.after_hours_forward_to || "",
        after_hours_greeting: config.after_hours_greeting || "",
      };
      await axios.put(`${API}/settings/phone-numbers/${phone.number_id}`, {
        label: config.label,
        assignment_type: config.assignment_type,
        assignment_id: config.assignment_id,
        location_id: config.location_id || "",
        is_active: config.is_active,
        purpose: config.purpose,
        call_routing_enabled: !!call_routing_enabled,
        auto_create_job_from_call: !!auto_create_job_from_call,
        call_flow: callFlow,
        config: rest,
      }, getAuthConfig());
      toast.success("Phone number settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sectionBtn = (s) => {
    const Icon = s.icon;
    const active = activeSection === s.id;
    return (
      <button
        key={s.id}
        onClick={() => setActiveSection(s.id)}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all w-full text-left ${
          active
            ? isDark ? "bg-[#FFCC00]/10 text-[#FFCC00]" : "bg-amber-50 text-amber-700 border border-amber-200"
            : isDark ? "text-white/50 hover:text-white/80 hover:bg-white/5" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
        }`}
        data-testid={`phone-cfg-section-${s.id}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="hidden sm:inline">{s.label}</span>
      </button>
    );
  };

  // Mobile section tabs
  const mobileSectionTab = (s) => {
    const Icon = s.icon;
    const active = activeSection === s.id;
    return (
      <button
        key={s.id}
        onClick={() => setActiveSection(s.id)}
        className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] font-medium transition-all shrink-0 ${
          active
            ? isDark ? "bg-[#FFCC00]/10 text-[#FFCC00]" : "bg-amber-50 text-amber-700"
            : isDark ? "text-white/40" : "text-gray-400"
        }`}
      >
        <Icon className="w-4 h-4" />
        {s.label}
      </button>
    );
  };

  if (!phone) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-3xl max-w-[95vw] p-0 overflow-hidden border-0 gap-0 ${isDark ? "bg-[#111113]" : "bg-white"}`}
        data-testid="phone-config-modal"
      >
        {/* Header */}
        <div className={`px-5 pt-5 pb-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-green-500/15" : "bg-green-50"}`}>
                <Phone className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-foreground font-mono">
                  {phone.phone_number}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">{config.label || "Configure your phone number"}</p>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Mobile section tabs */}
        <div className="sm:hidden overflow-x-auto px-3 py-2 border-b border-theme">
          <div className="flex gap-1">{SECTIONS.map(mobileSectionTab)}</div>
        </div>

        {/* Body */}
        <div className="flex min-h-[420px] max-h-[65vh]">
          {/* Desktop sidebar */}
          <div className={`hidden sm:flex flex-col w-44 shrink-0 p-3 gap-0.5 border-r ${isDark ? "border-white/5 bg-white/[0.01]" : "border-gray-100 bg-gray-50/50"}`}>
            {SECTIONS.map(sectionBtn)}
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-5 space-y-5">
              {/* ─── GENERAL ─────────────────────────────────── */}
              {activeSection === "general" && (
                <>
                  <SectionTitle title="General Settings" />
                  <Field label="Label / Friendly Name">
                    <Input value={config.label} onChange={e => set("label", e.target.value)} className="input-glass" placeholder="Main Line" data-testid="cfg-label" />
                  </Field>
                  {locations.length > 0 && (
                    <Field label="Business Location">
                      <Select value={config.location_id || "none"} onValueChange={v => set("location_id", v === "none" ? "" : v)}>
                        <SelectTrigger className="input-glass" data-testid="cfg-location">
                          <SelectValue placeholder="Not assigned" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                          <SelectItem value="none">Not assigned</SelectItem>
                          {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Calls to/from this number are tagged with this business location in Call History and on the incoming-call screen. Assigning also back-tags matching historical calls.
                      </p>
                    </Field>
                  )}
                  <Field label="Number Purpose">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "main", l: "Main Number", icon: Phone, desc: "Primary business line" },
                        { v: "job_source", l: "Job Source", icon: Briefcase, desc: "Track leads by source" },
                        { v: "dispatcher", l: "Dispatcher", icon: UserCheck, desc: "Assigned to a team member" },
                        { v: "call_masking", l: "Call Masking", icon: Shield, desc: "Techs call this number + job PIN to reach customers" },
                      ].map(p => (
                        <button key={p.v} onClick={() => set("purpose", p.v)}
                          className={`p-3 rounded-lg text-left transition-all border ${
                            config.purpose === p.v
                              ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40 text-[#FFCC00]" : "bg-amber-50 border-amber-400 text-amber-700"
                              : isDark ? "bg-white/[0.02] border-white/10 hover:border-white/20 text-muted-foreground" : "bg-white border-gray-200 hover:border-gray-300 text-gray-600"
                          }`}
                          data-testid={`purpose-${p.v}`}
                        >
                          <p.icon className="w-4 h-4 mb-1" />
                          <div className="text-xs font-medium">{p.l}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{p.desc}</div>
                        </button>
                      ))}
                    </div>
                  </Field>
                  {config.purpose === "call_masking" && (
                    <div className={`p-3 rounded-lg text-xs leading-relaxed ${isDark ? "bg-white/[0.03] border border-white/10 text-muted-foreground" : "bg-amber-50/60 border border-amber-200 text-gray-600"}`} data-testid="call-masking-purpose-note">
                      Technicians call this number and enter their 3-digit job PIN to be connected to the customer — the customer's real number stays hidden.
                      This number is also used as the account default when a technician doesn't have their own masking number assigned (Technicians → edit → Call Masking Number).
                    </div>
                  )}
                  {config.purpose === "job_source" && (
                    <Field label="Assign to Job Source">
                      <Select value={config.assignment_id} onValueChange={v => { set("assignment_id", v); set("assignment_type", "job_source"); }}>
                        <SelectTrigger className="input-glass"><SelectValue placeholder="Select job source..." /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                          {(jobSources || []).map(s => <SelectItem key={s.source_id} value={s.source_id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  {config.purpose === "dispatcher" && (
                    <Field label="Assign to Team Member">
                      <Select value={config.assignment_id} onValueChange={v => { set("assignment_id", v); set("assignment_type", "dispatcher"); }}>
                        <SelectTrigger className="input-glass"><SelectValue placeholder="Select team member..." /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                          {dispatchers.map(d => <SelectItem key={d.user_id} value={d.user_id}>{d.name || d.email}</SelectItem>)}
                          {technicians.map(t => <SelectItem key={t.technician_id} value={t.technician_id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Field label="Caller ID Name">
                    <Input value={config.caller_id_name} onChange={e => set("caller_id_name", e.target.value)} className="input-glass" placeholder="Your Business Name" data-testid="cfg-cnam" />
                    <p className="text-[10px] text-muted-foreground mt-1">Displayed on recipient's phone when you make outbound calls (CNAM)</p>
                  </Field>
                  <ToggleRow label="Active" desc="Enable or disable this phone number" checked={config.is_active} onChange={v => set("is_active", v)} testId="cfg-active" />
                </>
              )}

              {/* ─── CALL HANDLING ───────────────────────────── */}
              {activeSection === "call" && (
                <>
                  <SectionTitle title="Call Handling" />
                  <Field label="Call Routing Type">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "in_app", l: "In App Phone System", desc: "Ring dispatchers inside Fieldz AI", icon: Smartphone },
                        { v: "forward", l: "Forward", desc: "Forward to a single number", icon: PhoneOutgoing },
                        { v: "ivr", l: "IVR Menu", desc: "Press 1 for Sales, etc.", icon: Zap },
                      ].map(opt => (
                        <button key={opt.v} onClick={() => set("call_flow_type", opt.v)}
                          data-testid={`cfg-flow-${opt.v}`}
                          className={`p-3 rounded-lg text-left transition-all border flex items-start gap-2.5 ${
                            config.call_flow_type === opt.v
                              ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40" : "bg-amber-50 border-amber-400"
                              : isDark ? "bg-white/[0.02] border-white/10 hover:border-white/20" : "bg-white border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <opt.icon className={`w-4 h-4 mt-0.5 shrink-0 ${config.call_flow_type === opt.v ? isDark ? "text-[#FFCC00]" : "text-amber-600" : "text-muted-foreground"}`} />
                          <div>
                            <div className={`text-xs font-medium ${config.call_flow_type === opt.v ? "text-foreground" : "text-muted-foreground"}`}>{opt.l}</div>
                            <div className="text-[10px] text-muted-foreground">{opt.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </Field>

                  {/* In App Phone System config */}
                  {config.call_flow_type === "in_app" && (
                    <>
                      <Field label="Who should receive inbound calls?">
                        <div className="space-y-1.5" data-testid="in-app-recipients">
                          {eligibleRecipients.length === 0 && (
                            <p className="text-xs text-muted-foreground">No eligible users found.</p>
                          )}
                          {eligibleRecipients.map((p) => {
                            const selectedList = config.in_app_recipients || [];
                            const checked = selectedList.includes(p.person_id);
                            return (
                              <label key={p.person_id}
                                className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition-colors ${
                                  checked
                                    ? isDark ? "bg-emerald-500/10 border-emerald-500/40" : "bg-emerald-50 border-emerald-300"
                                    : isDark ? "bg-white/[0.02] border-white/10" : "bg-white border-gray-200"
                                }`}
                                data-testid={`in-app-recipient-${p.person_id}`}
                              >
                                <input type="checkbox" checked={checked}
                                  onChange={() => set("in_app_recipients",
                                    checked ? selectedList.filter(id => id !== p.person_id) : [...selectedList, p.person_id])} />
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-medium text-foreground">{p.name}</span>
                                  <span className="text-[10px] text-muted-foreground ml-1.5 uppercase">{p.role}</span>
                                </div>
                                <span className={`text-[10px] ${p.registered ? "text-emerald-500" : "text-zinc-500"}`}>
                                  {p.registered ? "● device ready" : "○ no device"}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="in-app-recipients-note">
                          <span className="font-semibold text-foreground/80">Only the users selected above will receive inbound calls for this phone number.</span>{" "}
                          Admins and Owners do <span className="font-semibold">not</span> ring automatically unless selected.
                          Leave everyone unchecked to ring all registered users on the account. Users must be Available with a live device to ring.
                        </p>
                      </Field>
                      <Field label="Ringing behavior">
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { v: "everyone", l: "Ring Everyone", desc: "All ring at once — first to answer wins" },
                            { v: "sequential", l: "Sequential", desc: "One at a time, in the order selected" },
                            { v: "round_robin", l: "Round Robin", desc: "Rotate who rings first on each call" },
                          ].map(opt => (
                            <button key={opt.v} onClick={() => set("in_app_ring_strategy", opt.v)}
                              data-testid={`ring-strategy-${opt.v}`}
                              className={`p-2.5 rounded-lg text-left transition-all border ${
                                (config.in_app_ring_strategy || "everyone") === opt.v
                                  ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40" : "bg-amber-50 border-amber-400"
                                  : isDark ? "bg-white/[0.02] border-white/10 hover:border-white/20" : "bg-white border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div className={`text-xs font-medium ${(config.in_app_ring_strategy || "everyone") === opt.v ? "text-foreground" : "text-muted-foreground"}`}>{opt.l}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>
                            </button>
                          ))}
                        </div>
                      </Field>
                      <div className="grid grid-cols-2 gap-3">
                        {(config.in_app_ring_strategy || "everyone") === "everyone" ? (
                          <Field label="Ring duration (seconds)">
                            <Input type="number" min="5" max="55" value={config.in_app_ring_timeout}
                              onChange={e => set("in_app_ring_timeout", e.target.value)} className="input-glass"
                              data-testid="in-app-ring-timeout" />
                          </Field>
                        ) : (
                          <Field label="Seconds per person">
                            <Input type="number" min="5" max="40" value={config.in_app_step_timeout}
                              onChange={e => set("in_app_step_timeout", e.target.value)} className="input-glass"
                              data-testid="in-app-step-timeout" />
                          </Field>
                        )}
                        <Field label="If nobody answers">
                          <Select value={config.in_app_fallback} onValueChange={v => set("in_app_fallback", v)}>
                            <SelectTrigger className="input-glass" data-testid="in-app-fallback"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="voicemail">Send to voicemail</SelectItem>
                              <SelectItem value="forward">Forward to external number</SelectItem>
                              <SelectItem value="hangup">Play message & hang up</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                      {config.in_app_fallback === "forward" && (
                        <Field label="Fallback forward number">
                          <Input value={config.fallback_forward_to} onChange={e => set("fallback_forward_to", e.target.value)}
                            className="input-glass" placeholder="+1 (555) 123-4567" data-testid="in-app-fallback-forward" />
                        </Field>
                      )}
                    </>
                  )}

                  {/* Forward config */}
                  {config.call_flow_type === "forward" && (
                    <Field label="Forward Calls To">
                      <Input value={config.forward_to} onChange={e => set("forward_to", e.target.value)} className="input-glass" placeholder="+1 (555) 123-4567" data-testid="cfg-forward-to" />
                    </Field>
                  )}

                  {/* IVR config */}
                  {config.call_flow_type === "ivr" && (
                    <>
                      <Field label="Greeting Message">
                        <Textarea value={config.ivr_greeting} onChange={e => set("ivr_greeting", e.target.value)} className="input-glass" rows={2} placeholder="Thank you for calling..." />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Tip: For pronunciation fixes of your business name, wrap the greeting in <code>&lt;speak&gt;…&lt;/speak&gt;</code> with SSML — e.g. <code>&lt;phoneme alphabet="ipa" ph="faɪəldz"&gt;Fieldz&lt;/phoneme&gt;</code>.
                        </p>
                      </Field>

                      <Field label="Menu Options">
                        <div className="space-y-2">
                          {(config.ivr_menu || []).map((item, i) => {
                            const updateItem = (patch) => {
                              const m = [...config.ivr_menu];
                              m[i] = { ...m[i], ...patch };
                              set("ivr_menu", m);
                            };
                            const removeItem = () => set("ivr_menu", config.ivr_menu.filter((_, j) => j !== i));
                            const action = item.action || "forward";
                            return (
                              <div key={i} className={`p-2.5 rounded-lg border space-y-2 ${isDark ? "bg-white/[0.02] border-white/5" : "bg-gray-50 border-gray-200"}`} data-testid={`ivr-option-${i}`}>
                                <div className="flex gap-2 items-center flex-wrap">
                                  <Input type="number" min="0" max="9" value={item.digit}
                                    onChange={e => updateItem({ digit: e.target.value.replace(/[^0-9]/g, "").slice(0,1) })}
                                    className="input-glass w-12 text-center" data-testid={`ivr-digit-${i}`} />
                                  <Input value={item.label || ""}
                                    onChange={e => updateItem({ label: e.target.value })}
                                    maxLength={60}
                                    className="input-glass flex-1 min-w-[140px]"
                                    placeholder="Label (e.g., Sales)" data-testid={`ivr-label-${i}`} />
                                  <select
                                    value={action}
                                    onChange={(e) => updateItem({ action: e.target.value })}
                                    className={`input-glass rounded-md px-2 py-1 text-sm ${isDark ? "bg-white/[0.04]" : "bg-white"}`}
                                    data-testid={`ivr-action-${i}`}
                                  >
                                    <option value="forward">Forward to number</option>
                                    <option value="voicemail">Go to voicemail</option>
                                    <option value="submenu">Open sub-menu</option>
                                    <option value="hangup">Say message & hang up</option>
                                  </select>
                                  <Button variant="ghost" size="icon" onClick={removeItem}
                                    className="h-8 w-8 text-red-400 shrink-0"
                                    data-testid={`ivr-remove-${i}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>

                                {/* Per-action config */}
                                {action === "forward" && (
                                  <>
                                    <Input value={item.forward_to || ""}
                                      onChange={e => updateItem({ forward_to: e.target.value })}
                                      className="input-glass" placeholder="Forward to number (+15551234567)" />
                                    <div className="flex items-center gap-2 pl-1">
                                      <Bell className="w-3 h-3 text-muted-foreground shrink-0" />
                                      <Input value={item.whisper_message || ""}
                                        onChange={e => updateItem({ whisper_message: e.target.value })}
                                        className="input-glass h-7 text-xs"
                                        placeholder="Whisper message played to the answering party..." />
                                    </div>
                                  </>
                                )}

                                {action === "hangup" && (
                                  <Input value={item.hangup_message || ""}
                                    onChange={e => updateItem({ hangup_message: e.target.value })}
                                    className="input-glass" placeholder="Message to speak before hanging up..." />
                                )}

                                {/* Submenu editor (1 level nesting) */}
                                {action === "submenu" && (
                                  <div className={`ml-3 mt-2 border-l-2 pl-3 space-y-2 ${isDark ? "border-white/10" : "border-gray-300"}`}>
                                    <Input value={item.submenu_greeting || ""}
                                      onChange={e => updateItem({ submenu_greeting: e.target.value })}
                                      className="input-glass"
                                      placeholder={`Sub-menu greeting (e.g. "You've reached billing.")`} />
                                    <div className="space-y-1.5">
                                      {(item.submenu || []).map((sub, si) => {
                                        const updateSub = (patch) => {
                                          const sm = [...(item.submenu || [])];
                                          sm[si] = { ...sm[si], ...patch };
                                          updateItem({ submenu: sm });
                                        };
                                        const removeSub = () => updateItem({ submenu: (item.submenu || []).filter((_, j) => j !== si) });
                                        const sAction = sub.action || "forward";
                                        return (
                                          <div key={si} className="space-y-1">
                                            <div className="flex gap-2 items-center">
                                              <Input type="number" min="0" max="9" value={sub.digit}
                                                onChange={e => updateSub({ digit: e.target.value.replace(/[^0-9]/g, "").slice(0,1) })}
                                                className="input-glass w-12 text-center" />
                                              <Input value={sub.label || ""}
                                                onChange={e => updateSub({ label: e.target.value })}
                                                className="input-glass flex-1" placeholder="Label" />
                                              <select value={sAction} onChange={e => updateSub({ action: e.target.value })}
                                                className={`input-glass rounded-md px-2 py-1 text-sm ${isDark ? "bg-white/[0.04]" : "bg-white"}`}>
                                                <option value="forward">Forward</option>
                                                <option value="voicemail">Voicemail</option>
                                                <option value="hangup">Hang up</option>
                                              </select>
                                              <Button variant="ghost" size="icon" onClick={removeSub} className="h-7 w-7 text-red-400 shrink-0">
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </div>
                                            {sAction === "forward" && (
                                              <Input value={sub.forward_to || ""}
                                                onChange={e => updateSub({ forward_to: e.target.value })}
                                                className="input-glass h-7 text-xs" placeholder="Forward to..." />
                                            )}
                                          </div>
                                        );
                                      })}
                                      <Button variant="ghost" size="sm"
                                        onClick={() => updateItem({ submenu: [...(item.submenu || []), { digit: ((item.submenu?.length) || 0) + 1, label: "", action: "forward", forward_to: "" }] })}
                                        className="h-7 text-xs">
                                        <Plus className="w-3 h-3 mr-1" /> Add sub-option
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <Button variant="ghost" size="sm"
                            onClick={() => set("ivr_menu", [...(config.ivr_menu || []), { digit: String(((config.ivr_menu?.length) || 0) + 1), label: "", action: "forward", forward_to: "", whisper_message: "" }])}
                            className={`h-8 text-xs ${isDark ? "text-[#FFCC00]" : "text-amber-600"}`}
                            data-testid="ivr-add-option">
                            <Plus className="w-3 h-3 mr-1" /> Add Menu Option
                          </Button>
                        </div>
                      </Field>

                      {/* Fallback if caller presses invalid digit / no input */}
                      <Field label="When caller doesn't pick a valid option">
                        <div className="space-y-2">
                          <p className="text-[11px] text-muted-foreground">
                            After the menu is re-read once, Fieldz AI will fall back to this action:
                          </p>
                          <select
                            value={config.ivr_default_action || "hangup"}
                            onChange={e => set("ivr_default_action", e.target.value)}
                            className={`input-glass w-full rounded-md px-3 py-2 text-sm ${isDark ? "bg-white/[0.04]" : "bg-white"}`}
                            data-testid="ivr-default-action"
                          >
                            <option value="hangup">Say goodbye &amp; hang up</option>
                            <option value="voicemail">Send to voicemail</option>
                            <option value="forward">Forward to main number</option>
                          </select>
                          {config.ivr_default_action === "forward" && (
                            <Input
                              value={config.ivr_default_forward_to || ""}
                              onChange={e => set("ivr_default_forward_to", e.target.value)}
                              className="input-glass"
                              placeholder="+1 (555) 123-4567"
                              data-testid="ivr-default-forward-to"
                            />
                          )}
                        </div>
                      </Field>
                    </>
                  )}

                  {/* Business hours routing (per-number, any flow type) */}
                  <Divider />
                  <ToggleRow label="Business Hours Routing"
                    desc="Route calls differently outside business hours — uses this number's location timezone (or account timezone)"
                    checked={config.business_hours_enabled}
                    onChange={v => set("business_hours_enabled", v)} testId="cfg-business-hours-toggle" />
                  {config.business_hours_enabled && (
                    <>
                      <Field label="Weekly schedule">
                        <div className="space-y-1.5" data-testid="business-hours-schedule">
                          {DAY_ORDER.map(d => {
                            const day = (config.business_hours_schedule || {})[d] || { enabled: false, start: "09:00", end: "17:00" };
                            const update = (patch) => set("business_hours_schedule", { ...config.business_hours_schedule, [d]: { ...day, ...patch } });
                            return (
                              <div key={d} className="flex items-center gap-2">
                                <label className="flex items-center gap-2 w-16 cursor-pointer">
                                  <input type="checkbox" checked={!!day.enabled} onChange={e => update({ enabled: e.target.checked })} data-testid={`bh-day-${d}`} />
                                  <span className="text-xs text-foreground">{DAY_LABELS[d]}</span>
                                </label>
                                {day.enabled ? (
                                  <>
                                    <Input type="time" value={day.start} onChange={e => update({ start: e.target.value })} className="input-glass h-8 w-28 text-xs" data-testid={`bh-start-${d}`} />
                                    <span className="text-xs text-muted-foreground">to</span>
                                    <Input type="time" value={day.end} onChange={e => update({ end: e.target.value })} className="input-glass h-8 w-28 text-xs" data-testid={`bh-end-${d}`} />
                                  </>
                                ) : (
                                  <span className="text-[11px] text-muted-foreground">Closed</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Field>
                      <Field label="After-hours action">
                        <Select value={config.after_hours_action} onValueChange={v => set("after_hours_action", v)}>
                          <SelectTrigger className="input-glass" data-testid="after-hours-action"><SelectValue /></SelectTrigger>
                          <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                            <SelectItem value="voicemail">Send to voicemail</SelectItem>
                            <SelectItem value="forward">Forward to a number</SelectItem>
                            <SelectItem value="ring_anyway">Ring normally (ignore hours)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      {config.after_hours_action === "voicemail" && (
                        <Field label="After-hours voicemail greeting">
                          <Textarea value={config.after_hours_greeting} onChange={e => set("after_hours_greeting", e.target.value)}
                            className="input-glass" rows={2} data-testid="after-hours-greeting" />
                        </Field>
                      )}
                      {config.after_hours_action === "forward" && (
                        <Field label="After-hours forward number">
                          <Input value={config.after_hours_forward_to} onChange={e => set("after_hours_forward_to", e.target.value)}
                            className="input-glass" placeholder="+1 (555) 987-6543" data-testid="after-hours-forward-to" />
                        </Field>
                      )}
                    </>
                  )}

                  <Divider />
                  <Field label="Ring Timeout (seconds)">
                    <div className="flex items-center gap-3">
                      {[15, 20, 30, 45, 60].map(t => (
                        <button key={t} onClick={() => set("ring_timeout", t)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                            config.ring_timeout === t
                              ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40 text-[#FFCC00]" : "bg-amber-50 border-amber-400 text-amber-700"
                              : isDark ? "border-white/10 text-muted-foreground hover:border-white/20" : "border-gray-200 text-gray-500 hover:border-gray-300"
                          }`}
                        >{t}s</button>
                      ))}
                    </div>
                  </Field>

                  {/* Whisper */}
                  {config.call_flow_type !== "ivr" && (
                    <Field label="Whisper Message" hint="Played to the agent before connecting the caller">
                      <Input value={config.whisper_message} onChange={e => set("whisper_message", e.target.value)} className="input-glass" placeholder='e.g., "Call from Google Ads"' data-testid="cfg-whisper" />
                    </Field>
                  )}

                  <ToggleRow label="Call Screening" desc="Announce caller info before connecting" checked={config.call_screening} onChange={v => set("call_screening", v)} testId="cfg-screening" />

                  {/* Inbound Caller ID */}
                  <Field label="Inbound Caller ID" hint="What the recipient sees when a customer calls this number">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "caller", l: "Customer's Number", desc: "Show the caller's actual phone number", icon: Phone },
                        { v: "business", l: "Business Number", desc: "Show this business phone number", icon: Building },
                      ].map(opt => (
                        <button key={opt.v} onClick={() => set("inbound_caller_id", opt.v)}
                          className={`p-3 rounded-lg text-left transition-all border flex items-start gap-2.5 ${
                            (config.inbound_caller_id || "business") === opt.v
                              ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40" : "bg-amber-50 border-amber-400"
                              : isDark ? "bg-white/[0.02] border-white/10 hover:border-white/20" : "bg-white border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`cfg-caller-id-${opt.v}`}
                        >
                          <opt.icon className={`w-4 h-4 mt-0.5 shrink-0 ${(config.inbound_caller_id || "business") === opt.v ? isDark ? "text-[#FFCC00]" : "text-amber-600" : "text-muted-foreground"}`} />
                          <div>
                            <div className={`text-xs font-medium ${(config.inbound_caller_id || "business") === opt.v ? "text-foreground" : "text-muted-foreground"}`}>{opt.l}</div>
                            <div className="text-[10px] text-muted-foreground">{opt.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </Field>

                  <ToggleRow label="Call Queue" desc="Hold callers in queue when all lines busy" checked={config.call_queue} onChange={v => set("call_queue", v)} testId="cfg-queue" />
                  {config.call_queue && (
                    <Field label="Hold Music">
                      <Select value={config.call_queue_music} onValueChange={v => set("call_queue_music", v)}>
                        <SelectTrigger className="input-glass"><SelectValue /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                          <SelectItem value="default">Default</SelectItem>
                          <SelectItem value="classical">Classical</SelectItem>
                          <SelectItem value="jazz">Jazz</SelectItem>
                          <SelectItem value="none">Silence</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <ToggleRow label="Simultaneous Ring" desc="Ring multiple numbers at the same time" checked={config.simultaneous_ring} onChange={v => set("simultaneous_ring", v)} testId="cfg-simring" />
                  {config.simultaneous_ring && (
                    <Field label="Additional Numbers to Ring">
                      <div className="space-y-2">
                        {(config.simultaneous_numbers || []).map((num, i) => (
                          <div key={i} className="flex gap-2">
                            <Input value={num} onChange={e => { const n = [...config.simultaneous_numbers]; n[i] = e.target.value; set("simultaneous_numbers", n); }} className="input-glass" placeholder="+1 (555) 123-4567" />
                            <Button variant="ghost" size="icon" onClick={() => set("simultaneous_numbers", config.simultaneous_numbers.filter((_, j) => j !== i))} className="h-9 w-9 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" onClick={() => set("simultaneous_numbers", [...(config.simultaneous_numbers || []), ""])} className={`h-8 text-xs ${isDark ? "text-[#FFCC00]" : "text-amber-600"}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add Number
                        </Button>
                      </div>
                    </Field>
                  )}
                </>
              )}

              {/* ─── IN-APP CALLING (Call Routing) ────────────── */}
              {activeSection === "routing" && (
                <>
                  <SectionTitle title="In-App Calling — Per-Number Routing" />
                  <p className="text-xs text-muted-foreground mb-2">
                    Route inbound calls to <strong>this number</strong> through the Fieldz AI app on every registered device, and turn unknown callers into Job Leads automatically.
                  </p>
                  <ToggleRow
                    label="Enable In-App Calling for this number"
                    desc="Inbound calls to this number will ring the Fieldz app on every registered device. Outbound calls placed from the app will use this number as caller ID."
                    checked={!!config.call_routing_enabled}
                    onChange={v => set("call_routing_enabled", v)}
                    testId="cfg-call-routing-enabled"
                  />
                  <ToggleRow
                    label="Auto-create Job Lead from inbound call"
                    desc="When an UNKNOWN caller dials this number, automatically create a Customer + a pending Job tagged 'From Call'. The recording is auto-attached when the call ends."
                    checked={!!config.auto_create_job_from_call}
                    onChange={v => set("auto_create_job_from_call", v)}
                    testId="cfg-auto-create-lead"
                  />
                  <div className="rounded-lg p-3 text-[11px] flex items-start gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-300 mt-2">
                    <Phone className="w-3.5 h-3.5 shrink-0 mt-px" />
                    <p className="leading-relaxed">
                      Account-wide settings (registered devices, ring-all-devices) live under <strong>Phone Settings → Call Routing</strong>.
                      When this number's In-App Calling is on AND no devices are registered, the call falls back to the regular Call Flow above.
                    </p>
                  </div>
                </>
              )}

              {/* ─── SMS & MESSAGING ─────────────────────────── */}
              {activeSection === "sms" && (
                <>
                  <SectionTitle title="SMS & Messaging" />
                  <ToggleRow label="Auto-Reply to SMS" desc="Automatically reply to incoming text messages" checked={config.sms_auto_reply_enabled} onChange={v => set("sms_auto_reply_enabled", v)} testId="cfg-autoreply" />
                  {config.sms_auto_reply_enabled && (
                    <Field label="Auto-Reply Message">
                      <Textarea value={config.sms_auto_reply_message} onChange={e => set("sms_auto_reply_message", e.target.value)} className="input-glass" rows={2} />
                    </Field>
                  )}
                  <ToggleRow label="After-Hours SMS Reply" desc="Send a different reply outside business hours" checked={config.sms_after_hours_reply} onChange={v => set("sms_after_hours_reply", v)} testId="cfg-afterhours-sms" />
                  {config.sms_after_hours_reply && (
                    <Field label="After-Hours Message">
                      <Textarea value={config.sms_after_hours_message} onChange={e => set("sms_after_hours_message", e.target.value)} className="input-glass" rows={2} />
                    </Field>
                  )}
                  <ToggleRow label="Missed Call SMS" desc="Auto-text callers when you miss their call" checked={config.missed_call_sms} onChange={v => set("missed_call_sms", v)} testId="cfg-missedcall-sms" />
                  {config.missed_call_sms && (
                    <Field label="Missed Call Message">
                      <Textarea value={config.missed_call_sms_message} onChange={e => set("missed_call_sms_message", e.target.value)} className="input-glass" rows={2} />
                    </Field>
                  )}
                  <Divider />
                  <Field label="SMS Signature" hint="Appended to every outgoing message">
                    <Input value={config.sms_signature} onChange={e => set("sms_signature", e.target.value)} className="input-glass" placeholder="— Sent from YourBusiness" data-testid="cfg-sms-sig" />
                  </Field>
                  <ToggleRow label="Opt-Out Handling" desc="Auto-handle STOP/UNSUBSCRIBE keywords" checked={config.sms_opt_out_enabled} onChange={v => set("sms_opt_out_enabled", v)} testId="cfg-optout" />
                  <Divider />
                  <Field label="Keyword Auto-Responses" hint='e.g., Text "HOURS" to get business hours'>
                    <div className="space-y-2">
                      {(config.keyword_responses || []).map((kw, i) => (
                        <div key={i} className="flex gap-2">
                          <Input value={kw.keyword} onChange={e => { const k = [...config.keyword_responses]; k[i].keyword = e.target.value; set("keyword_responses", k); }} className="input-glass w-28" placeholder="HOURS" />
                          <Input value={kw.response} onChange={e => { const k = [...config.keyword_responses]; k[i].response = e.target.value; set("keyword_responses", k); }} className="input-glass flex-1" placeholder="Response message..." />
                          <Button variant="ghost" size="icon" onClick={() => set("keyword_responses", config.keyword_responses.filter((_, j) => j !== i))} className="h-9 w-9 text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => set("keyword_responses", [...(config.keyword_responses || []), { keyword: "", response: "" }])} className={`h-8 text-xs ${isDark ? "text-[#FFCC00]" : "text-amber-600"}`}>
                        <Plus className="w-3 h-3 mr-1" /> Add Keyword
                      </Button>
                    </div>
                  </Field>
                </>
              )}

              {/* ─── VOICEMAIL ──────────────────────────────── */}
              {activeSection === "voicemail" && (
                <>
                  <SectionTitle title="Voicemail" />
                  <ToggleRow label="Enable Voicemail" desc="Allow callers to leave a voicemail" checked={config.voicemail_enabled} onChange={v => set("voicemail_enabled", v)} testId="cfg-vm-enabled" />
                  {config.voicemail_enabled && (
                    <>
                      <Field label="Greeting Message" hint="Text-to-speech or custom greeting played to callers">
                        <Textarea value={config.voicemail_greeting} onChange={e => set("voicemail_greeting", e.target.value)} className="input-glass" rows={2} placeholder="Please leave a message..." />
                      </Field>
                      <Field label="Max Voicemail Length">
                        <div className="flex items-center gap-3">
                          {[30, 60, 120, 180, 300].map(s => (
                            <button key={s} onClick={() => set("voicemail_max_length", s)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                config.voicemail_max_length === s
                                  ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/40 text-[#FFCC00]" : "bg-amber-50 border-amber-400 text-amber-700"
                                  : isDark ? "border-white/10 text-muted-foreground" : "border-gray-200 text-gray-500"
                              }`}
                            >{s < 60 ? `${s}s` : `${s / 60}m`}</button>
                          ))}
                        </div>
                      </Field>
                      <ToggleRow label="Voicemail to Email" desc="Send voicemail recordings to email" checked={config.voicemail_to_email} onChange={v => set("voicemail_to_email", v)} testId="cfg-vm-email" />
                      {config.voicemail_to_email && (
                        <Field label="Email Address">
                          <Input type="email" value={config.voicemail_email} onChange={e => set("voicemail_email", e.target.value)} className="input-glass" placeholder="you@company.com" />
                        </Field>
                      )}
                      <ToggleRow label="Voicemail Transcription" desc="Automatically transcribe voicemails to text" checked={config.voicemail_transcription} onChange={v => set("voicemail_transcription", v)} testId="cfg-vm-transcription" />
                    </>
                  )}
                </>
              )}

              {/* ─── RECORDING ─────────────────────────────── */}
              {activeSection === "recording" && (
                <>
                  <SectionTitle title="Call Recording & Compliance" />
                  <ToggleRow label="Call Recording" desc="Record all calls on this number" checked={config.call_recording} onChange={v => set("call_recording", v)} testId="cfg-recording" />
                  {config.call_recording && (
                    <>
                      <ToggleRow label="Consent Announcement" desc="Play a recording disclaimer before connecting" checked={config.recording_consent_enabled} onChange={v => set("recording_consent_enabled", v)} testId="cfg-consent" />
                      {config.recording_consent_enabled && (
                        <Field label="Consent Message">
                          <Textarea value={config.recording_consent_message} onChange={e => set("recording_consent_message", e.target.value)} className="input-glass" rows={2} />
                        </Field>
                      )}
                    </>
                  )}
                  <Divider />
                  <Field label="Hold Music">
                    <Select value={config.hold_music} onValueChange={v => set("hold_music", v)}>
                      <SelectTrigger className="input-glass"><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-[#1a1a1f] border-white/10" : ""}>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="classical">Classical</SelectItem>
                        <SelectItem value="jazz">Jazz</SelectItem>
                        <SelectItem value="rock">Upbeat</SelectItem>
                        <SelectItem value="none">Silence</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </>
              )}

              {/* ─── SPAM & BLOCKING ───────────────────────── */}
              {activeSection === "spam" && (
                <>
                  <SectionTitle title="Spam & Call Blocking" />
                  <ToggleRow label="Spam Filter" desc="Automatically filter suspected spam calls" checked={config.spam_filter} onChange={v => set("spam_filter", v)} testId="cfg-spam" />
                  <ToggleRow label="Block International Calls" desc="Reject calls from international numbers" checked={config.block_international} onChange={v => set("block_international", v)} testId="cfg-intl" />
                  <Divider />
                  <Field label="Blocked Numbers" hint="Calls from these numbers will be rejected">
                    <div className="space-y-2">
                      {(config.blocked_numbers || []).map((num, i) => (
                        <div key={i} className="flex gap-2">
                          <Input value={num} onChange={e => { const n = [...config.blocked_numbers]; n[i] = e.target.value; set("blocked_numbers", n); }} className="input-glass font-mono" placeholder="+1 (555) 000-0000" />
                          <Button variant="ghost" size="icon" onClick={() => set("blocked_numbers", config.blocked_numbers.filter((_, j) => j !== i))} className="h-9 w-9 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => set("blocked_numbers", [...(config.blocked_numbers || []), ""])} className={`h-8 text-xs ${isDark ? "text-[#FFCC00]" : "text-amber-600"}`}>
                        <Plus className="w-3 h-3 mr-1" /> Block a Number
                      </Button>
                    </div>
                  </Field>
                </>
              )}

              {/* ─── NOTIFICATIONS ─────────────────────────── */}
              {activeSection === "notifications" && (
                <>
                  <SectionTitle title="Notifications" />
                  <ToggleRow label="Missed Call Email Alert" desc="Get emailed when a call is missed" checked={config.missed_call_email} onChange={v => set("missed_call_email", v)} testId="cfg-miss-email" />
                  {config.missed_call_email && (
                    <Field label="Email Address">
                      <Input type="email" value={config.missed_call_email_address} onChange={e => set("missed_call_email_address", e.target.value)} className="input-glass" placeholder="you@company.com" />
                    </Field>
                  )}
                  <ToggleRow label="Daily Call Summary" desc="Receive a daily summary of all calls/texts" checked={config.daily_summary} onChange={v => set("daily_summary", v)} testId="cfg-daily" />
                  {config.daily_summary && (
                    <Field label="Summary Email">
                      <Input type="email" value={config.daily_summary_email} onChange={e => set("daily_summary_email", e.target.value)} className="input-glass" placeholder="you@company.com" />
                    </Field>
                  )}
                  <ToggleRow label="Real-Time Notifications" desc="Push notifications for incoming calls to team" checked={config.realtime_notifications} onChange={v => set("realtime_notifications", v)} testId="cfg-realtime" />
                  {config.realtime_notifications && (
                    <Field label="Notify Team Members">
                      <div className="space-y-1.5">
                        {[...dispatchers, ...technicians.map(t => ({ user_id: t.technician_id, name: t.name, email: t.email }))].map(member => {
                          const memberId = member.user_id || member.technician_id;
                          const selected = (config.notify_team_members || []).includes(memberId);
                          return (
                            <button
                              key={memberId}
                              onClick={() => {
                                const current = config.notify_team_members || [];
                                set("notify_team_members", selected ? current.filter(id => id !== memberId) : [...current, memberId]);
                              }}
                              className={`w-full flex items-center justify-between p-2 rounded-lg border text-sm transition-all ${
                                selected
                                  ? isDark ? "bg-[#FFCC00]/10 border-[#FFCC00]/30" : "bg-amber-50 border-amber-300"
                                  : isDark ? "bg-white/[0.02] border-white/5 hover:border-white/15" : "bg-white border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <span className={selected ? "text-foreground" : "text-muted-foreground"}>{member.name || member.email}</span>
                              {selected && <Check className={`w-4 h-4 ${isDark ? "text-[#FFCC00]" : "text-amber-600"}`} />}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 px-5 py-3 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={save} disabled={saving} className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"} data-testid="phone-cfg-save">
            {saving ? "Saving..." : <><Save className="w-4 h-4 mr-1.5" /> Save Settings</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Shared sub-components ──────────────────────────────────
const SectionTitle = ({ title }) => (
  <h3 className="text-sm font-semibold text-foreground tracking-tight">{title}</h3>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const ToggleRow = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-theme bg-theme-surface">
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const Divider = () => <div className="border-t border-theme" />;

export default PhoneConfigModal;
